import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, { useRef, useEffect, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { cgtag } from 'utilities/gtag';
import { isMobileOrTablet } from 'utilities/userAgent';

const useStyles: any = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        padding: theme.spacing(0, 0, 2, 0),
        [theme.breakpoints.down('md')]: {
          paddingBottom: theme.spacing(1.5),
        },
      },
      scrollContainer: {
        overflowX: 'scroll',
      },
      scrollWrapper: {
        position: 'relative',
      },
      container: {
        display: 'flex',
        flexFlow: 'row',
        boxSizing: 'border-box',
        '&:after': {
          /* JT - the following block allows the child margin to be included in the scroll */
          content: "'.'",
          color: 'transparent',
          fontSize: 0,
          width: 0,
        },
        '&>*': {
          margin: theme.spacing(1.5, 0.5),
          '&:first-child': {
            marginLeft: theme.spacing(1),
          },
          '&:last-child': {
            marginRight: theme.spacing(1),
          },
        },
      },
      removeItemContainerPadding: {
        '&>*': {
          '&:first-child': {
            marginLeft: theme.spacing(0),
          },
          '&:last-child': {
            marginRight: theme.spacing(0),
          },
        },
      },
      title: {
        cursor: 'pointer',
        [theme.breakpoints.down('md')]: {
          paddingBottom: theme.spacing(0.5),
        },
      },
      arrowLeft: {
        position: 'absolute',
        display: 'grid',
        placeContent: 'center',
        top: 0,
        left: 0,
        height: '100%',
        //background: 'linear-gradient(90deg, #121212 0%, rgba(18, 18, 18, 0.63) 59.42%, rgba(18, 18, 18, 0) 100%)',
      },
      arrowRight: {
        position: 'absolute',
        display: 'grid',
        placeContent: 'center',
        top: 0,
        right: 0,
        height: '100%',
        //background: 'linear-gradient(270deg, #121212 0%, rgba(18, 18, 18, 0.63) 59.42%, rgba(18, 18, 18, 0) 100%)',
      },
      cover: {
        position: 'absolute',
        top: '0',
        bottom: '0',
        left: '0',
        right: '0',
        background: 'rgba(0, 0, 0, 0.75)',
        '& .MuiButton-root': {
          width: '115px',
          height: '35px',
          border: `1px solid ${theme.palette.text.primary}`,
          borderRadius: '25px',
          boxSizing: 'border-box',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
        },
      },
      arrowHeight: {
        height: 'calc(100% - 30px)',
      },
      button: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      },
    }),
  { name: 'ContentSlider' },
);

const ContentSlider: React.FC<{
  title?: string;
  items: any[];
  arrowOnHover?: boolean;
  source?: 'corporate' | 'redeem';
  onClick?: (product: any) => void;
  hideTitle?: boolean;
  onClickTitle?: () => void;
  padFirstAndLast?: boolean;
}> = ({ title, items, onClick, children, arrowOnHover = false, source, hideTitle = false, onClickTitle, padFirstAndLast = false }) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = !isMobileOrTablet();

  const [arrowDisplayedRight, setArrowDisplayedRight] = useState(true);
  const [arrowDisplayedLeft, setArrowDisplayedLeft] = useState(false);
  const [arrowDisplayed, setArrowDisplayed] = useState(false);

  const checkArrow = () => {
    const element = containerRef.current;
    const offsetRight = element ? element.scrollWidth - element.clientWidth - element.scrollLeft : 0;
    const offsetLeft = element ? element.scrollLeft : 0;
    setArrowDisplayedRight(offsetRight > 20);
    setArrowDisplayedLeft(offsetLeft > 20);
  };

  useEffect(() => {
    const timeout = setTimeout(checkArrow, 200);
    return () => clearTimeout(timeout);
  }, [containerRef]);

  const handleSlide = (right: boolean) => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        left: right ? containerRef.current.scrollLeft + containerRef.current.offsetWidth : containerRef.current.scrollLeft - containerRef.current.offsetWidth,
        behavior: 'smooth',
      });
      cgtag('slider_interaction', {
        slider_title: title,
        slide_direction: right ? 'right' : 'left',
      });
    }
  };

  //only show arrow on desktop
  const showArrow = isDesktop && (!arrowOnHover || arrowDisplayed);

  return (
    <Box onMouseEnter={() => setArrowDisplayed(true)} onMouseLeave={() => setArrowDisplayed(false)} sx={{ flexGrow: 1 }} className={styles.root}>
      {hideTitle === false && title && title.length && (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" className={styles.title} onClick={onClickTitle}>
            {title}
          </Typography>
        </Stack>
      )}
      <div className={styles.scrollWrapper}>
        <ScrollContainer
          className={styles.scrollContainer}
          vertical={false}
          innerRef={containerRef}
          onEndScroll={(...args: any) => {
            checkArrow();
          }}
        >
          <div className={clsx(styles.container, padFirstAndLast ? null : styles.removeItemContainerPadding)}>
            {
              // @ts-ignore
              children(items, onClick)
            }
          </div>
        </ScrollContainer>

        <Fade in={showArrow && arrowDisplayedRight}>
          <div className={clsx(styles.arrowRight, source && styles.arrowHeight)}>
            <IconButton disableRipple className={styles.button} onClick={() => handleSlide(true)} size="large">
              <ArrowForwardIosIcon />
            </IconButton>
          </div>
        </Fade>
        <Fade in={showArrow && arrowDisplayedLeft}>
          <div className={clsx(styles.arrowLeft, source && styles.arrowHeight)}>
            <IconButton disableRipple sx={{ transform: 'scaleX(-1)' }} className={styles.button} onClick={() => handleSlide(false)} size="large">
              <ArrowForwardIosIcon />
            </IconButton>
          </div>
        </Fade>
      </div>
    </Box>
  );
};

export default ContentSlider;
